// Vorgangsdaten und QR-Inhalt – rein, für Server und Oberfläche. Format nach der Datensatzbeschreibung der Kassen
// (DSFinV-K 2.4, Anhang I „Kassenbeleg-V1“ und Nr. 2 „QR-Code“), wie in docs/kasse/SPEZIFIKATION.md §2 und §3.3:
//
//   Beleg^<Brutto 19 %>_<7 %>_<§ 24 Nr. 3>_<§ 24 Nr. 1>_<0 %>^<Betrag>:Bar_<Betrag>:Unbar
//
// Beträge mit genau zwei Nachkommastellen und Punkt; Zahlungen nur Bar/Unbar, je Art zusammengefasst, Bar zuerst,
// Beträge von 0,00 entfallen. Ein Gutschein ist KEINE Zahlart: Seine Einlösung mindert das Feld seines Satzes
// (Mehrzweck: 0 %).

export const VORGANGSTYPEN = ['Beleg', 'AVTransfer', 'AVBestellung', 'AVTraining', 'AVBelegabbruch', 'AVSachbezug', 'AVSonstige', 'AVRechnung'] as const
export type Vorgangstyp = (typeof VORGANGSTYPEN)[number]

/** Die fünf Felder der Bruttobeträge in der Reihenfolge der Vorgangsdaten. */
export const SLOTS = ['normal', 'reduced', 'average3', 'average1', 'zero'] as const
export type Slot = (typeof SLOTS)[number]
export type SlotAmounts = Record<Slot, number>

export const emptySlots = (): SlotAmounts => ({ normal: 0, reduced: 0, average3: 0, average1: 0, zero: 0 })

/**
 * Feld eines Steuersatzes: allgemeiner Satz (19 %, im zweiten Halbjahr 2020 16 %), ermäßigter Satz (7 % bzw. 5 %),
 * Durchschnittssätze nach § 24 UStG (10,7 % und 5,5 %), alles ohne Steuer ins letzte Feld.
 */
export function slotOfRate(rateBp: number): Slot {
  switch (rateBp) {
    case 1900:
    case 1600:
      return 'normal'
    case 700:
    case 500:
      return 'reduced'
    case 1070:
      return 'average3'
    case 550:
      return 'average1'
    case 0:
      return 'zero'
    default:
      throw new RangeError(`Für den Steuersatz ${rateBp / 100} % gibt es kein Feld in den Vorgangsdaten.`)
  }
}

/** Betrag in Cent als „-12.34“: genau zwei Nachkommastellen, Punkt, ohne Tausendertrennung, ohne Gleitkomma. */
export function formatAmount(cents: number): string {
  if (!Number.isSafeInteger(cents)) throw new RangeError(`Kein ganzzahliger Centbetrag: ${cents}`)
  const abs = Math.abs(cents)
  return `${cents < 0 ? '-' : ''}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`
}

export type PaymentKind = 'Bar' | 'Unbar'

export interface KassenbelegData {
  vorgangstyp: Vorgangstyp
  grossCents: SlotAmounts
  payments: readonly { kind: PaymentKind; amountCents: number }[]
}

export const PROCESS_TYPE = 'Kassenbeleg-V1'

/** Vorgangsdaten eines Kassenbelegs: 'Beleg^37.89_11.24_0.00_0.00_0.00^49.13:Bar'. */
export function kassenbelegProcessData(data: KassenbelegData): string {
  const amounts = SLOTS.map((slot) => formatAmount(data.grossCents[slot])).join('_')
  const byKind = new Map<PaymentKind, number>()
  for (const payment of data.payments) byKind.set(payment.kind, (byKind.get(payment.kind) ?? 0) + payment.amountCents)
  const payments = (['Bar', 'Unbar'] as const)
    .filter((kind) => (byKind.get(kind) ?? 0) !== 0)
    .map((kind) => `${formatAmount(byKind.get(kind) as number)}:${kind}`)
    .join('_')
  return `${data.vorgangstyp}^${amounts}^${payments}`
}

/** Summe der Felder – muss bei jedem Kassenbeleg der Summe der Zahlungen entsprechen (außer Abbruch). */
export const slotTotal = (slots: SlotAmounts): number => SLOTS.reduce((sum, slot) => sum + slots[slot], 0)

/** Unix-Sekunden (Originalformat der Einheit) → '2026-10-14T08:15:02.000Z'. Ein ISO-Zeitpunkt bleibt ISO. */
export function unixToIso(raw: string): string {
  if (/^\d+$/.test(raw)) return new Date(Number(raw) * 1000).toISOString()
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) throw new RangeError(`Kein Zeitpunkt: ${raw}`)
  return date.toISOString()
}

export interface QrFields {
  clientSerial: string
  processType: string
  processData: string
  txNo: number
  signatureCounter: number
  startIso: string
  endIso: string
  algorithm: string
  timeFormat: string
  signature: string
  publicKey: string
}

/** Präfix des QR-Inhalts einer nicht zertifizierten Einheit (Demo, Simulator, Testumgebung). */
export const DEMO_QR_PREFIX = 'DEMO;'

/**
 * Inhalt des QR-Codes: 'V0;Kassen-Seriennummer;Vorgangsart;Vorgangsdaten;Transaktionsnummer;Signaturzähler;Start;
 * Ende;Algorithmus;Zeitformat;Signatur;öffentlicher Schlüssel'. Ohne zertifizierte Einheit beginnt er mit 'DEMO;' und
 * ist damit bewusst kein gültiger Code.
 */
export function qrContent(fields: QrFields, certified: boolean): string {
  const content = [
    'V0',
    fields.clientSerial,
    fields.processType,
    fields.processData,
    String(fields.txNo),
    String(fields.signatureCounter),
    fields.startIso,
    fields.endIso,
    fields.algorithm,
    fields.timeFormat,
    fields.signature,
    fields.publicKey,
  ].join(';')
  return certified ? content : `${DEMO_QR_PREFIX}${content}`
}

/** Zerlegt einen QR-Inhalt wieder in seine Felder (für Tests und die Prüfung gegen den Wert des Anbieters). */
export function parseQrContent(content: string): { demo: boolean; fields: QrFields } | null {
  const demo = content.startsWith(DEMO_QR_PREFIX)
  const body = demo ? content.slice(DEMO_QR_PREFIX.length) : content
  const parts = body.split(';')
  // Vorgangsdaten enthalten kein ';' (Felder mit '^', '_' und ':') – zwölf Teile genau
  if (parts.length !== 12 || parts[0] !== 'V0') return null
  const [, clientSerial, processType, processData, txNo, counter, startIso, endIso, algorithm, timeFormat, signature, publicKey] = parts as [string, ...string[]]
  return {
    demo,
    fields: {
      clientSerial: clientSerial as string,
      processType: processType as string,
      processData: processData as string,
      txNo: Number(txNo),
      signatureCounter: Number(counter),
      startIso: startIso as string,
      endIso: endIso as string,
      algorithm: algorithm as string,
      timeFormat: timeFormat as string,
      signature: signature as string,
      publicKey: publicKey as string,
    },
  }
}
